/**
 * S5: a customer's bank disputing a card payment (a chargeback), as Stripe
 * reports it. Stripe's statuses are stored as Stripe sends them, since it adds
 * new ones (`prevented` is recent); what each means for the money is decided
 * here, in one place, and a status not known here is treated as an open
 * formal dispute until someone looks at it.
 */
export const DISPUTE_STATUSES = [
  // An inquiry: the bank asks before deciding; no money has moved.
  'warning_needs_response',
  'warning_under_review',
  'warning_closed',
  // A chargeback: the money and Stripe's dispute fee have left the balance.
  'needs_response',
  'under_review',
  'won',
  'lost',
  // Stopped by Stripe's prevention tools, which refund the payment (to be seen in test mode).
  'prevented',
] as const
export type KnownDisputeStatus = (typeof DISPUTE_STATUSES)[number]

/**
 * Stripe's words for a refund refused or failed because the payment is
 * disputed: the two error codes of a refund asked for on a disputed charge,
 * and the failure reason of one that was pending when the dispute came.
 * Such a refund never lets a bank transfer stand in for Stripe: the bank may
 * be giving the money back already.
 */
export const DISPUTE_REFUSALS: readonly string[] = [
  'charge_disputed',
  'refund_disputed_payment',
  'charge_for_pending_refund_disputed',
]

export interface DisputeEffect {
  /** The bank gave the money back to the customer: the booking owes it again. */
  returned: boolean
  /**
   * No refund by any means: while a chargeback is open, Stripe's dispute
   * process is the only way money goes back ("You can't issue a refund outside
   * the dispute process while the dispute is open"), and a lost one has gone.
   */
  blocksRefund: boolean
  /** No bank transfer in Stripe's place: the dispute could still give the money back. */
  blocksTransfer: boolean
  /** Still open: the booking cannot be closed while it is. */
  open: boolean
  /** Waiting for the company's answer in Stripe's Dashboard, by its deadline. */
  needsAnswer: boolean
}

const CLOSED: DisputeEffect = {
  returned: false,
  blocksRefund: false,
  blocksTransfer: false,
  open: false,
  needsAnswer: false,
}

export function disputeEffect(status: string): DisputeEffect {
  switch (status) {
    case 'warning_needs_response':
      // An inquiry: a refund through Stripe settles it, a transfer would pay twice if it escalates.
      return { ...CLOSED, blocksTransfer: true, open: true, needsAnswer: true }
    case 'warning_under_review':
      return { ...CLOSED, blocksTransfer: true, open: true }
    case 'needs_response':
      return { ...CLOSED, blocksRefund: true, blocksTransfer: true, open: true, needsAnswer: true }
    case 'under_review':
      return { ...CLOSED, blocksRefund: true, blocksTransfer: true, open: true }
    case 'lost':
      // Stripe: a lost dispute can still turn into a won one; until it does, the money is gone.
      return { ...CLOSED, returned: true, blocksRefund: true, blocksTransfer: true }
    case 'won':
    case 'warning_closed':
    case 'prevented':
      return CLOSED
    default:
      return { ...CLOSED, blocksRefund: true, blocksTransfer: true, open: true }
  }
}

export interface DisputeLike {
  status: string
  amountCents: number
}

/** What several disputes add up to: those of one payment, or of one booking. */
export interface DisputesSummary {
  /** Given back to the customer by the bank, in cents: owed again. */
  lostCents: number
  /** Held or taken by the bank (a chargeback open or lost): not ours to give back. */
  withheldCents: number
  blocksRefund: boolean
  blocksTransfer: boolean
  open: boolean
}

export function disputesSummary(disputes: readonly DisputeLike[]): DisputesSummary {
  let lostCents = 0
  let withheldCents = 0
  let blocksRefund = false
  let blocksTransfer = false
  let open = false
  for (const dispute of disputes) {
    const effect = disputeEffect(dispute.status)
    if (effect.returned) lostCents += dispute.amountCents
    if (effect.blocksRefund) withheldCents += dispute.amountCents
    blocksRefund ||= effect.blocksRefund
    blocksTransfer ||= effect.blocksTransfer
    open ||= effect.open
  }
  return { lostCents, withheldCents, blocksRefund, blocksTransfer, open }
}

/** For the SQL that sums the money given back: the statuses that count as returned. */
export const RETURNED_DISPUTE_STATUSES: readonly string[] = DISPUTE_STATUSES.filter(
  (status) => disputeEffect(status).returned,
)

/**
 * For the SQL that finds open disputes: the known statuses that are NOT open.
 * Open is `status not in` these, so a status Stripe adds counts as open, as
 * `disputeEffect` treats it; a list of the open ones would miss it.
 */
export const ENDED_DISPUTE_STATUSES: readonly string[] = DISPUTE_STATUSES.filter(
  (status) => !disputeEffect(status).open,
)

/**
 * For the database's refund guard (migration 0028): the known statuses that
 * put no limit on giving money back, the dispute over and the money kept.
 * Any other, a status Stripe adds included, allows a refund through Stripe
 * only, if at all.
 */
export const CLEARED_DISPUTE_STATUSES: readonly string[] = DISPUTE_STATUSES.filter(
  (status) => !disputeEffect(status).blocksTransfer,
)

/**
 * For the SQL that finds money a bank holds or took: the known statuses under
 * which a refund may still be made (through Stripe at least). Any other, a
 * status Stripe adds included, withholds the disputed money.
 */
export const REFUNDABLE_DISPUTE_STATUSES: readonly string[] = DISPUTE_STATUSES.filter(
  (status) => !disputeEffect(status).blocksRefund,
)

/** Waiting for the company's answer by `evidence_due_by`. */
export const ANSWER_DISPUTE_STATUSES: readonly string[] = DISPUTE_STATUSES.filter(
  (status) => disputeEffect(status).needsAnswer,
)

/** Whether Stripe sent a status this code does not know: shown to the Owner as such. */
export function isKnownDisputeStatus(status: string): status is KnownDisputeStatus {
  return (DISPUTE_STATUSES as readonly string[]).includes(status)
}
